import {
  TextInput,
  Grid,
  Stack,
  ScrollArea,
  Button,
  Select,
  LoadingOverlay,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { showNotification } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { customLoader } from "components/utilities/loader";
import { UPDATE_SUPPLIER_Commistion } from "apollo/mutuations";
import { GET_ALL_SUPPLIERS, GET_SUPPLIERS_Business } from "apollo/queries";

const CommissionEditModal = ({ commistion, setOpenedEdit, isSme = false }) => {
  const [suppliers, setSuppliers] = useState([]);
  const form = useForm({
    initialValues: {
      supplier_id: "",
      commission_rate: "",
      payment_mode: "",
      calculation_type: "",
      subscription_fee_amount: "",
      fee_interval: "",
      fee_on_date: "",
    },
    validate: {
      supplier_id: (value) => (!value ? "Supplier is required" : null),
      payment_mode: (value) =>
        !value
          ? "Payment mode is required"
          : !["COMMISSION", "HYBRID", "SUBSCRIPTION"].includes(value)
            ? "Invalid payment mode"
            : null,
      commission_rate: (value, values) => {
        if (
          values.payment_mode === "COMMISSION" ||
          values.payment_mode === "HYBRID"
        ) {
          if (value === "") return "Commission rate is required";
          if (isNaN(value)) return "Commission rate must be a valid number";
          if (parseFloat(value) <= 0)
            return "Commission rate must be a positive number";
        }
        return null;
      },
      calculation_type: (value, values) => {
        if (
          values.payment_mode === "COMMISSION" ||
          values.payment_mode === "HYBRID"
        ) {
          if (!value) return "Calculation type is required";
          if (!["FLAT", "PERCENTAGE"].includes(value))
            return "Invalid calculation type";
        }
        return null;
      },
      subscription_fee_amount: (value, values) => {
        if (
          values.payment_mode === "SUBSCRIPTION" ||
          values.payment_mode === "HYBRID"
        ) {
          if (value === "") return "Subscription fee amount is required";
          if (isNaN(value))
            return "Subscription fee amount must be a valid number";
          if (parseFloat(value) <= 0)
            return "Subscription fee amount must be a positive number";
        }
        return null;
      },
      fee_interval: (value, values) => {
        if (
          values.payment_mode === "SUBSCRIPTION" ||
          values.payment_mode === "HYBRID"
        ) {
          if (!value) return "Fee interval is required";
          if (!["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(value))
            return "Invalid fee interval";
        }
        return null;
      },
      fee_on_date: (value, values) => {
        if (
          values.payment_mode === "SUBSCRIPTION" ||
          values.payment_mode === "HYBRID"
        ) {
          if (!value) return "Fee on date is required";
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
            return "Fee on date must be in YYYY-MM-DD format";
        }
        return null;
      },
    },
  });

  const { data, loading, refetch } = useQuery(GET_ALL_SUPPLIERS, {
    variables: {
      first: 100000,
      page: 1,
      search: "",
      is_sme: isSme,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onCompleted(data) {
      const arr = data.getSuppliers.data.map((item) => ({
        label: item.user.name,
        value: item.id,
      }));
      setSuppliers(arr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Suppliers",
      });
    },
  });

  const [updateSupplier, { loading: supplierLoading }] = useMutation(
    UPDATE_SUPPLIER_Commistion,
  );
  useEffect(() => {
    if (commistion) {
      form.setValues({
        commission_rate: commistion.commission_rate?.toString() || "",
        payment_mode: commistion.payment_mode || "",
        calculation_type: commistion.calculation_type || "",
        subscription_fee_amount:
          commistion.subscription_fee_amount?.toString() || "",
        fee_interval: commistion.fee_interval || "",
        fee_on_date: commistion.fee_on_date || "",
      });
    }
  }, [commistion]);
  useEffect(() => {
    const selectedWarehouse = suppliers.find(
      (wh) => wh.value === commistion.supplier?.id,
    );
    form.setValues({
      supplier_id: selectedWarehouse?.value?.toString(),
    });
  }, [suppliers]);
  useEffect(() => {
    refetch();
  }, []);

  const { height } = useViewportSize();

  const submit = () => {
    if (form.validate().hasErrors) return;

    const variables = {
      id: commistion.id,
      supplier_id: form.getInputProps("supplier_id").value,
      payment_mode: form.getInputProps("payment_mode").value,
    };

    if (form.values.payment_mode === "COMMISSION") {
      variables.commission_rate = parseFloat(
        form.getInputProps("commission_rate").value,
      );
      variables.calculation_type = form.getInputProps("calculation_type").value;
    } else if (form.values.payment_mode === "SUBSCRIPTION") {
      variables.subscription_fee_amount = parseFloat(
        form.getInputProps("subscription_fee_amount").value,
      );
      variables.calculation_type = form.getInputProps("calculation_type").value;
      variables.fee_interval = form.getInputProps("fee_interval").value;
      variables.fee_on_date = form.getInputProps("fee_on_date").value;
    } else if (form.values.payment_mode === "HYBRID") {
      variables.commission_rate = parseFloat(
        form.getInputProps("commission_rate").value,
      );
      variables.calculation_type = form.getInputProps("calculation_type").value;
      variables.subscription_fee_amount = parseFloat(
        form.getInputProps("subscription_fee_amount").value,
      );
      variables.fee_interval = form.getInputProps("fee_interval").value;
      variables.fee_on_date = form.getInputProps("fee_on_date").value;
    }

    updateSupplier({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Commission Updated Successfully",
        });
        setOpenedEdit(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to update commission",
        });
        setOpenedEdit(false);
      },
    });
  };

  const handleSupplierChange = (val) => {
    form.setFieldValue("supplier_id", val);
  };

  const paymentMode = form.getInputProps("payment_mode").value;

  return (
    <>
      <LoadingOverlay
        visible={loading || supplierLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={4}>
                <Select
                  searchable
                  required
                  data={suppliers}
                  value={form.getInputProps("supplier_id").value}
                  onChange={handleSupplierChange}
                  label="Supplier"
                  placeholder="Pick a supplier"
                  error={form.errors.supplier_id}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Select
                  required
                  data={[
                    { value: "COMMISSION", label: "Commission" },
                    { value: "HYBRID", label: "Hybrid" },
                    { value: "SUBSCRIPTION", label: "Subscription" },
                  ]}
                  {...form.getInputProps("payment_mode")}
                  label="Payment Mode"
                  placeholder="Select payment mode"
                  error={form.errors.payment_mode}
                />
              </Grid.Col>
              {(paymentMode === "COMMISSION" || paymentMode === "HYBRID") && (
                <Grid.Col span={4}>
                  <TextInput
                    required
                    type="number"
                    label="Commission Rate"
                    placeholder="Commission Rate"
                    {...form.getInputProps("commission_rate")}
                    error={form.errors.commission_rate}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={4}>
                <Select
                  required
                  data={[
                    { value: "FLAT", label: "Flat" },
                    { value: "PERCENTAGE", label: "Percentage" },
                  ]}
                  {...form.getInputProps("calculation_type")}
                  label="Calculation Type"
                  placeholder="Select calculation type"
                  error={form.errors.calculation_type}
                />
              </Grid.Col>
              {(paymentMode === "SUBSCRIPTION" || paymentMode === "HYBRID") && (
                <>
                  <Grid.Col span={4}>
                    <TextInput
                      required
                      type="number"
                      label="Subscription Fee Amount"
                      placeholder="Subscription Fee Amount"
                      {...form.getInputProps("subscription_fee_amount")}
                      error={form.errors.subscription_fee_amount}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Select
                      required
                      data={[
                        { value: "DAILY", label: "Daily" },
                        { value: "WEEKLY", label: "Weekly" },
                        { value: "MONTHLY", label: "Monthly" },
                        { value: "YEARLY", label: "Yearly" },
                      ]}
                      {...form.getInputProps("fee_interval")}
                      label="Fee Interval"
                      placeholder="Select fee interval"
                      error={form.errors.fee_interval}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      required
                      type="date"
                      label="Fee on Date"
                      placeholder="YYYY-MM-DD"
                      {...form.getInputProps("fee_on_date")}
                      error={form.errors.fee_on_date}
                    />
                  </Grid.Col>
                </>
              )}
            </Grid>
            <Grid>
              <Grid.Col span={12}>
                <Button
                  style={{
                    width: "20%",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  fullWidth
                  type="submit"
                  color="blue"
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default CommissionEditModal;
